import { checkTimer, getSession, isLoggedIn } from "@/utils/auth";
import { BASIC, CLASSIC, ENTERPRISE, ESSENTIAL, PRO, SECONDARY, SPOT, SPOT_CON } from "@/utils/clientTypes";
import SegmentAnalytics from "@/utils/segmentAnalytics";
import Vue from "vue";
import VueRouter, { isNavigationFailure } from "vue-router";

Vue.use(VueRouter);

// vue-router 3.1+ rechaza la promesa de push/replace ante fallas de navegación
// benignas: NavigationDuplicated (navegar a la ruta actual), redirect desde un
// guard, navegación cancelada. En llamados fire-and-forget (sin callbacks) eso
// termina como unhandledrejection y ensucia Sentry. Tragamos sólo esas fallas
// de navegación — cualquier otro error se re-lanza. Patrón canónico para esta
// versión de vue-router (los callers que pasan callbacks conservan el flujo
// original). Ref: vue-router#2881.
["push", "replace"].forEach((method) => {
    const original = VueRouter.prototype[method];
    VueRouter.prototype[method] = function (location, onResolve, onReject) {
        if (onResolve || onReject) {
            return original.call(this, location, onResolve, onReject);
        }
        return original.call(this, location).catch((err) => {
            if (isNavigationFailure(err)) return err;
            return Promise.reject(err);
        });
    };
});

const routes = [
    {
        path: "/",
        name: "Login",
        alias: ["/login", "login"],
        component: () => import("@/views/login/Login.vue"),
    },
    {
        path: "/login-as-other-user",
        name: "Login as other user",
        component: () => import("@/views/login/Login.vue"),
        props: () => {
            return { isAnalist: true }
        }
    },
    {
        path: "/create-another-user/:uuid",
        name: "Create another user for an existing company",
        component: () => import("@/views/login/RegisterAnother.vue"),
    },
    {
        path: "/registerchita",
        name: "RegisterChita",
        component: () => import("@/views/login/RegisterChita.vue"),
    },
    {
        path: "/registerbasicfree",
        name: "RegisterBasic",
        component: () => import("@/views/login/RegisterFast.vue"),
        props: () => {
            return { type: BASIC }
        }
    },
    {
        path: "/registeressential",
        name: "RegisterEssential",
        component: () => import("@/views/login/RegisterFast.vue"),
        props: () => {
            return { type: ESSENTIAL }
        }
    },
    {
        path: "/registerenterprise",
        name: "RegisterEnterprise",
        component: () => import("@/views/login/RegisterFast.vue"),
        props: () => {
            return { type: ENTERPRISE }
        }
    },
    {
        path: "/recoveryconfirm",
        name: "RecoveryConfirm",
        component: () => import("@/views/login/RecoveryConfirm.vue"),
    },
    {
        path: "/recoverypassword",
        name: "RecoveryPassword",
        component: () => import("@/views/login/RecoveryPassword.vue"),
    },
    {
        path: "/changepassword/:token",
        name: "ChangePassword",
        component: () => import("@/views/login/ChangePassword.vue"),
    },
    {
        path: "/confirm",
        name: "Confirm",
        component: () => import("@/views/login/Confirm.vue"),
        props: (route) => Object.assign({}, route.query, route.params),
    },
    {
        path: "/thankyou",
        name: "Thankyou",
        component: () => import("@/views/login/Thankyou.vue"),
    },
    {
        path: "/go-to-plan",
        name: "Choose plan",
        meta: { requiresAuth: true },
    },
    {
        path: "/panel-analista",
        name: "panel-analista",
        component: () => import("@/views/PanelAnalista.vue"),
        meta: { requiresAuth: true, authRequired: [BASIC, CLASSIC, ENTERPRISE, PRO, SECONDARY, SPOT, SPOT_CON, ESSENTIAL] },
    },
    {
        path: "/info-company",
        name: "companyData",
        component: () => import("@/views/CompanyInfo.vue"),
        meta: { requiresAuth: true, authRequired: [BASIC, CLASSIC, ENTERPRISE, PRO, SECONDARY, SPOT, SPOT_CON, ESSENTIAL] },
        props: true,
    },
    {
        path: "/dashboard",
        name: CLASSIC,
        component: () => import("@/views/Dashboard.vue"),
        meta: { requiresAuth: true, authRequired: [CLASSIC, ESSENTIAL] },
    },
    {
        path: "/dashboard",
        name: BASIC,
        component: () => import("@/views/Dashboard.vue"),
        meta: { requiresAuth: true, authRequired: [BASIC] },
    },
    {
        path: "/dashboard",
        name: PRO,
        component: () => import("@/views/Dashboard.vue"),
        meta: { requiresAuth: true, authRequired: [PRO] },
    },
    {
        path: "/dashboard",
        name: ENTERPRISE,
        component: () => import("@/views/Dashboard.vue"),
        meta: { requiresAuth: true, authRequired: [ENTERPRISE] },
    },
    {
        path: "/dashboard",
        name: ESSENTIAL,
        component: () => import("@/views/Dashboard.vue"),
        meta: { requiresAuth: true, authRequired: [ESSENTIAL] },
    },
    {
        path: "/dashboard",
        name: SECONDARY,
        component: () => import("@/views/Dashboard.vue"),
        meta: { requiresAuth: true, authRequired: [SECONDARY] },
    },
    {
        path: "/plan-basic",
        redirect: '/dashboard',
    },
    {
        path: "/plan-essential",
        redirect: '/dashboard',
    },
    {
        path: "/plan-enterprise",
        redirect: '/dashboard',
    },
    {
        path: "/plan-pro",
        redirect: '/dashboard',
    },
    {
        path: "/filldata",
        name: "FillData",
        component: () => import("@/views/FillData.vue"),
        meta: { requiresAuth: true, authRequired: [ESSENTIAL, BASIC] },
    },
    {
        path: "/404",
        name: "Error404",
        component: () => import("@/views/errors/Error404.vue"),
    },
    {
        path: "*",
        name: "ErrorPage",
        component: () => import("@/views/errors/ErrorPage.vue"),
    },
];

const router = new VueRouter({
    routes,
});

router.beforeEach(async (to, from, next) => {
    checkTimer();
    const isLogged = await isLoggedIn();
    new SegmentAnalytics().page(to.name);
    if (isLogged) {
        let session = getSession();
        if (session) {
            if (to.name.startsWith("Register") || to.name === "Login") {
                next({
                    name: "Choose plan",
                });
            } else if (to.name === "Choose plan" || to.path === "/") {
                // Redirigir desde un guard se hace con next({...}), NO con
                // router.push: push deja la navegación actual pendiente y, si
                // apunta a la ruta donde ya estamos, rechaza con
                // NavigationDuplicated (unhandledrejection).
                if (session.user.user_type == 'ANALISTA' && session.user.is_staff == true) {
                    next({
                        name: 'panel-analista',
                    });
                } else {
                    let existsRoute = routes.find(
                        (route) => route.name === session.user.client_type
                    );
                    if (existsRoute) {
                        next({
                            name: session.user.client_type,
                        });
                    } else {
                        next({
                            name: "Error404",
                        });
                    }
                }
            } else if (to.matched.some((record) => record.meta.requiresAuth)) {
                if (
                    session.user.user_type != 'ANALISTA' &&
                    to.meta.authRequired.indexOf(session.user.client_type) < 0
                ) {
                    next({
                        name: "Choose plan",
                    });
                } else {
                    next();
                }
            } else {
                next();
            }
        }
    } else if (to.matched.some((record) => record.meta.requiresAuth)) {
        next({
            path: "/",
            name: "Login",
            query: { redirect: "/" },
        });
    } else {
        next();
    }
});

export default router;
